import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  UserPlus, Copy, CheckCircle2, Clock, XCircle, Link2, Share2, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralsTab() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchReferrals = useCallback(async () => {
    try {
      const [codeRes, refRes] = await Promise.all([
        axios.get(`${API}/referral/code`, { withCredentials: true }),
        axios.get(`${API}/referral/my-referrals`, { withCredentials: true }),
      ]);
      setData({ code: codeRes.data.code, ...refRes.data });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReferrals(); }, [fetchReferrals]);

  const referralLink = data?.code ? `${window.location.origin}/apply?ref=${data.code}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(data?.code || '');
    toast.success('Referral code copied!');
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-slate-400">Loading...</div>;
  }

  const statusIcon = (s) => {
    if (s === 'approved') return <CheckCircle2 size={12} className="text-emerald-500" />;
    if (s === 'rejected') return <XCircle size={12} className="text-red-400" />;
    return <Clock size={12} className="text-amber-500" />;
  };
  const statusColor = (s) => {
    if (s === 'approved') return 'bg-emerald-50 text-emerald-600 border-emerald-200';
    if (s === 'rejected') return 'bg-red-50 text-red-500 border-red-200';
    return 'bg-amber-50 text-amber-600 border-amber-200';
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="tab-referrals">
      <h1 className="text-3xl font-['Cabinet_Grotesk'] font-bold mb-1 text-slate-900">Referrals</h1>
      <p className="text-sm text-slate-500 mb-8">Refer agents and track your impact.</p>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-slate-200 p-5 rounded-xl">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold mb-2">Total Referrals</div>
          <div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-slate-900">{data?.total || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 p-5 rounded-xl">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold mb-2">Approved</div>
          <div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-emerald-600">{data?.approved || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 p-5 rounded-xl">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold mb-2">Pending</div>
          <div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-amber-500">{data?.pending || 0}</div>
        </div>
      </div>

      {/* Referral Code Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="referral-code-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#EFF3FF] flex items-center justify-center">
            <Share2 size={18} className="text-[#002FA7]" />
          </div>
          <div>
            <h3 className="text-base font-['Cabinet_Grotesk'] font-semibold text-slate-900">Your Referral Code</h3>
            <p className="text-xs text-slate-400">Share this with agents interested in joining Krieger.</p>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 font-mono text-lg font-bold text-[#002FA7] tracking-wider" data-testid="referral-code">
            {data?.code || '—'}
          </div>
          <Button variant="outline" onClick={copyCode} className="border-slate-200 text-slate-600 rounded-lg shrink-0" data-testid="copy-code-btn">
            <Copy size={14} className="mr-1" /> Code
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={referralLink}
            className="bg-slate-50 border-slate-200 text-slate-600 text-xs rounded-lg"
            data-testid="referral-link"
          />
          <Button onClick={copyLink} className={`rounded-lg shrink-0 text-xs transition-all ${copied ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#002FA7] hover:bg-[#002075]'} text-white`} data-testid="copy-link-btn">
            {copied ? <><CheckCircle2 size={12} className="mr-1" /> Copied!</> : <><Link2 size={12} className="mr-1" /> Copy Link</>}
          </Button>
        </div>
      </div>

      {/* Referrals List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="referrals-list">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800 font-['Cabinet_Grotesk']">Referred Agents</h3>
        </div>
        {!data?.referrals || data.referrals.length === 0 ? (
          <div className="p-10 text-center">
            <Users size={32} className="text-slate-200 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No referrals yet. Share your link to get started!</p>
          </div>
        ) : (
          <div>
            {data.referrals.map(ref => (
              <div key={ref.id} className="px-6 py-3 border-b border-slate-50 flex items-center justify-between" data-testid={`referral-${ref.id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-800">{ref.first_name} {ref.last_name}</div>
                  <div className="text-xs text-slate-400">{ref.email} · {ref.preferred_location}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[10px] ${statusColor(ref.status)}`}>
                    {statusIcon(ref.status)} <span className="ml-1">{ref.status}</span>
                  </Badge>
                  <span className="text-[10px] text-slate-300">{new Date(ref.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
